import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import ConsentForm from "common/forms/ConsentForm";
import { getSelectListForPlanForm as getSelectList } from "common/forms/utils";
import { getDownloadFileName } from "common/utils/downloadAsPdf";
import { FORM_TYPES } from "common/utils/formUtility";
import {
  getDetailsById,
  getLessonPlanFormAPI,
} from "components/listOfValues/utils";
import * as R from "ramda";
import { useEffect, useRef, useState } from "react";
import { colors } from "theme/colors";
import { LessonReadOnlyData } from "./ManageLessonPlan";

import LOVSelect from "common/components/LOVSelect/LOVSelect";
import {
  LOVDataKeys,
  LOVKeyMap,
  LOVSelectKeys,
} from "common/components/SelectDropDown/SelectDropDownUtils";
import { useReactToPrint } from "react-to-print";

import ReactLinkify from "react-linkify";

const useStyle = makeStyles((theme) => ({
  heading: {
    color: "#1b2945",
    marginTop: "2rem",
    marginBottom: "1rem",
  },
  readOnlyContainer: {
    backgroundColor: colors.blue1,
    padding: "1rem",
    color: colors.gray1,
    marginBottom: "2rem",
  },
  readOnlyTitle: {
    fontSize: "20px",
  },
  btn: {
    "&.MuiButton-root": {
      marginRight: "1rem",
    },
  },
  visibleTextField: {
    "&.MuiInputBase-input": {
      visibility: "visible",
      overflow: "visible",
    },
  },
  sequenceTh: {
    "&.MuiTableCell-sizeSmall": {
      width: "100px",
    },
  },
  tlmCell: {
    width: "20%",
    maxWidth: "20%",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    "& ol": {
      paddingLeft: 16,
      margin: 0,
    },
    "& li": {
      wordBreak: "break-word",
      overflowWrap: "break-word",
    },
  },
}));

const isEmptyValue = (val) =>
  R.isNil(val) ||
  (typeof val === "string" && R.trim(val) === "") ||
  (Array.isArray(val) && R.isEmpty(val));

const EmptyFallback = ({ value, fallback = "No response added", children }) =>
  isEmptyValue(value) ? (
    <Typography color="textSecondary">{fallback}</Typography>
  ) : (
    children
  );

const LessonPlanReadView = ({ data, userRoles, isPlanSummary }) => {
  const printRef = useRef();
  const classes = useStyle();
  const [lessonPlanData, setLessonData] = useState({});
  const downloadFileName = getDownloadFileName({
    data: data,
    templateId: "lesson",
  });

  useEffect(() => {
    data?.id && getPlanFormData(data?.id);
  }, [data]);

  const getPlanFormData = async (id) => {
    const res = isPlanSummary
      ? await getLessonPlanFormAPI(id)
      : await getDetailsById("/lesson-plan", id);
    setLessonData(isPlanSummary ? res : res?.lessonPlanForm);
  };

  const isPreClass = R.pathOr("", ["type"], lessonPlanData) === "PRECLASS";

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: downloadFileName,
  });

  return (
    <Box id="lessonPlanForm" ref={printRef} sx={{ padding: "1rem" }}>
      {R.isEmpty(lessonPlanData) ? (
        <h3 className={classes.heading} align={"left"}>
          No Data available
        </h3>
      ) : (
        <Box>
          <LessonReadOnlyData data={data} downloadAsPdf={handlePrint} />
          {!isPreClass && (
            <Box>
              <h3 className={classes.heading} align={"left"}>
                Periods
              </h3>
              <EmptyFallback
                value={R.pathOr("", ["periods"], lessonPlanData)}
                fallback="No response added"
              >
                <TextField
                  type="number"
                  id="multiline-static"
                  value={R.pathOr("", ["periods"], lessonPlanData)}
                  disabled={true}
                />
              </EmptyFallback>
              <h3 className={classes.heading} align={"left"}>
                Learning Objectives for the Lesson(Inform Students about what
                they are going to learn today) SWBA
              </h3>
              <h4 align={"left"}>
                Concept Related Objectives (Remembering & Recalling)
              </h4>
              <h5>Students will be able to –</h5>
              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="conceptObjective"
              >
                <EmptyFallback
                  value={R.pathOr(
                    "",
                    ["learningObjectiveForm", "conceptObjective"],
                    lessonPlanData
                  )}
                  fallback="No response added"
                >
                  {R.pathOr(
                    "",
                    ["learningObjectiveForm", "conceptObjective"],
                    lessonPlanData
                  )}
                </EmptyFallback>
              </Box>
              <br />
              <br />
              <h4 align={"left"}>
                Skill Related Objectives (Applying, Analysing, Evaluating,
                Creating)
              </h4>
              <h5>Students will be able to –</h5>
              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="skillObjective"
              >
                <EmptyFallback
                  value={R.pathOr(
                    "",
                    ["learningObjectiveForm", "skillObjective"],
                    lessonPlanData
                  )}
                  fallback="No response added"
                >
                  {R.pathOr(
                    "",
                    ["learningObjectiveForm", "skillObjective"],
                    lessonPlanData
                  )}
                </EmptyFallback>
              </Box>
              <br />
              <br />
              <h4 align={"left"}>Values Related Objectives</h4>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <h5 align={"left"}>Value of the Month</h5>
                  <Box fullWidth id="symbioticLearning">
                    <EmptyFallback
                      value={R.pathOr(
                        "",
                        [
                          "learningObjectiveForm",
                          "valuesObjectives",
                          "symbioticLearning",
                        ],
                        lessonPlanData
                      )}
                      fallback="No response added"
                    >
                      <Typography style={{ whiteSpace: "pre-line" }}>
                        {R.pathOr(
                          "",
                          [
                            "learningObjectiveForm",
                            "valuesObjectives",
                            "symbioticLearning",
                          ],
                          lessonPlanData
                        )}
                      </Typography>
                    </EmptyFallback>
                  </Box>
                </Grid>
              </Grid>

              <br />

              <br />

              <h3 className={classes.heading} align={"left"}>
                Keywords
              </h3>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: "background.paper",
                }}
              >
                <EmptyFallback
                  value={R.pathOr("", ["keywords"], lessonPlanData)}
                  fallback="No response added"
                >
                  <Typography style={{ whiteSpace: "pre-line" }}>
                    {R.pathOr("", ["keywords"], lessonPlanData)}
                  </Typography>
                </EmptyFallback>
              </Box>
              <br />
              <br />

              <Box>
                <h3 className={classes.heading} align={"left"}>
                  Main Content / Core / Introducing New Knowledge
                </h3>
                {R.isEmpty(R.propOr([], "mainContents", lessonPlanData)) ? (
                  <Typography color="textSecondary">
                    No response added
                  </Typography>
                ) : (
                  <TableContainer component={Paper}>
                    <Table
                      sx={{ minWidth: 650, tableLayout: "fixed" }}
                      size="small"
                      aria-label="a dense table"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell style={{ width: "20%" }}>
                            Introduction Activities
                          </TableCell>
                          <TableCell style={{ width: "50%" }}>
                            Sequence of Teaching Learning Activities
                          </TableCell>
                          <TableCell style={{ width: "10%" }} align="right">
                            Student Activities{" "}
                          </TableCell>
                          <TableCell
                            className={classes.tlmCell}
                            align="left"
                          >
                            TLM & Reference
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {R.map((item) => (
                          <TableRow
                            key={item.sequenceOfTla}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell component="th" scope="row">
                              {item.introductionActivity}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {item.sequenceOfTla}
                            </TableCell>
                            <TableCell align="right">
                              {item.studentActivity}
                            </TableCell>
                            <TableCell
                              className={classes.tlmCell}
                              align="left"
                            >
                              <ol>
                                {item.tlmList?.map((tlmItem) => (
                                  <li
                                    key={tlmItem.id}
                                    style={{ marginBottom: 10 }}
                                  >
                                    <Typography style={{ fontWeight: 600 }}>
                                      {tlmItem.name}
                                    </Typography>
                                    <ReactLinkify>
                                      <Typography
                                        style={{
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                          overflowWrap: "break-word",
                                        }}
                                      >
                                        {tlmItem.reference}
                                      </Typography>
                                    </ReactLinkify>
                                  </li>
                                ))}
                              </ol>
                            </TableCell>
                          </TableRow>
                        ))(R.propOr([], "mainContents", lessonPlanData))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
              <br />
              <br />
              <h3 className={classes.heading} align={"left"}>
                Differentiated Teaching & Learning Strategies
              </h3>
              {R.isEmpty(R.propOr([], "teachingStrategies", lessonPlanData)) ? (
                <Typography color="textSecondary">
                  No response added
                </Typography>
              ) : (
                <FormControl className={classes.inputFormControl} fullWidth>
                  <InputLabel id="demo-simple-select-outlined-label">
                    <b>Differentiated Teaching & Learning Strategies</b>
                  </InputLabel>
                  <LOVSelect
                    formType={FORM_TYPES.LESSON_PLAN_FORM}
                    selectKey={LOVSelectKeys.DIFF_TEACHING_STRATEGIES}
                    dataKey={LOVDataKeys.DIFF_TEACHING_STRATEGIES}
                    lovKey={LOVKeyMap.DIFF_TEACHING_STRATEGIES}
                    value={R.propOr([], "teachingStrategies", lessonPlanData)}
                    multiselect={true}
                    selectObj={true}
                    getSelectList={getSelectList}
                    isReadOnly={true}
                    showList={true}
                  />
                </FormControl>
              )}
              <br />
              <br />
              <h3 className={classes.heading} align={"left"}>
                Summarization / Consolidation / Check for Understanding /
                Opportunity for Applying, Practicing and Reviewing
              </h3>
              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="summarization"
              >
                <EmptyFallback
                  value={R.pathOr("", ["summarization"], lessonPlanData)}
                  fallback="No response added"
                >
                  {R.pathOr("", ["summarization"], lessonPlanData)}
                </EmptyFallback>
              </Box>

              <h3 className={classes.heading} align={"left"}>
                Real Life Connect / Cross Curricular Links / Subject Integration
              </h3>
              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="realLifeConnect"
              >
                <EmptyFallback
                  value={R.pathOr("", ["realLifeConnect"], lessonPlanData)}
                  fallback="No response added"
                >
                  {R.pathOr("", ["realLifeConnect"], lessonPlanData)}
                </EmptyFallback>
              </Box>
              <h3 className={classes.heading} align={"left"}>
                Assessment (AFL/ Self / Peer)
              </h3>
              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="assessmentProjectTask"
              >
                <EmptyFallback
                  value={R.pathOr(
                    "",
                    ["assessmentProjectTask"],
                    lessonPlanData
                  )}
                  fallback="No response added"
                >
                  {R.pathOr("", ["assessmentProjectTask"], lessonPlanData)}
                </EmptyFallback>
              </Box>
              <br />
              <br />

              <h3 className={classes.heading} align={"left"}>
                Project / Home Assignment / Extension Task
              </h3>

              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="assignmentProjectTask"
              >
                <EmptyFallback
                  value={R.pathOr("", ["assignmentProjectTask"], lessonPlanData)}
                  fallback="No response added"
                >
                  {R.pathOr("", ["assignmentProjectTask"], lessonPlanData)}
                </EmptyFallback>
              </Box>
              <br />
              <br />

              <h3 className={classes.heading} align={"left"}>
                CONSENTS
              </h3>
              <ConsentForm
                page={"lessonPlan"}
                userRoles={userRoles}
                consentData={R.propOr([], "consents", lessonPlanData)}
                isReadOnly={true}
              />
            </Box>
          )}
          {isPreClass && (
            <Box>
              <h3 className={classes.heading} align={"left"}>
                Periods
              </h3>
              <EmptyFallback
                value={R.pathOr("", ["periods"], lessonPlanData)}
                fallback="No response added"
              >
                <TextField
                  type="number"
                  id="multiline-static"
                  value={R.pathOr("", ["periods"], lessonPlanData)}
                  disabled={true}
                />
              </EmptyFallback>

              <Box>
                <h3 className={classes.heading} align={"left"}>
                  Teaching Learning Material
                </h3>
                {R.isEmpty(R.propOr([], "tlmList", lessonPlanData)) ? (
                  <Typography color="textSecondary">
                    No response added
                  </Typography>
                ) : (
                  R.map((item) => {
                  return (
                    <Grid container spacing={3}>
                      <Grid item xs={3}>
                        <Typography>{item.name}</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <ReactLinkify>
                          <Typography style={{ whiteSpace: "pre-line" }}>
                            {item.reference}
                          </Typography>
                        </ReactLinkify>
                      </Grid>
                    </Grid>
                  );
                })(R.propOr([], "tlmList", lessonPlanData))
                )}
              </Box>
              <h3 className={classes.heading} align={"left"}>
                Learning Objectives for the Lesson(Inform Students about what
                they are going to learn today) SWBA
              </h3>
              <h4 align={"left"}>
                Concept Related Objectives (Remembering & Recalling)
              </h4>
              <h5>Students will be able to –</h5>
              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="conceptObjective"
              >
                <EmptyFallback
                  value={R.pathOr(
                    "",
                    ["learningObjectiveForm", "conceptObjective"],
                    lessonPlanData
                  )}
                  fallback="No response added"
                >
                  {R.pathOr(
                    "",
                    ["learningObjectiveForm", "conceptObjective"],
                    lessonPlanData
                  )}
                </EmptyFallback>
              </Box>
              <br />
              <br />
              <h4 align={"left"}>
                Skill Related Objectives (Applying, Analysing, Evaluating,
                Creating)
              </h4>
              <h5>Students will be able to –</h5>
              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="skillObjective"
              >
                <EmptyFallback
                  value={R.pathOr(
                    "",
                    ["learningObjectiveForm", "skillObjective"],
                    lessonPlanData
                  )}
                  fallback="No response added"
                >
                  {R.pathOr(
                    "",
                    ["learningObjectiveForm", "skillObjective"],
                    lessonPlanData
                  )}
                </EmptyFallback>
              </Box>
              <br />
              <br />
              <h4 align={"left"}>
                Facts / Information Related Objectives (Remembering and
                Understanding)
              </h4>
              <h5>Students will be able to –</h5>
              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="factObjective"
              >
                <EmptyFallback
                  value={R.pathOr(
                    "",
                    ["learningObjectiveForm", "factObjective"],
                    lessonPlanData
                  )}
                  fallback="No response added"
                >
                  {R.pathOr(
                    "",
                    ["learningObjectiveForm", "factObjective"],
                    lessonPlanData
                  )}
                </EmptyFallback>
              </Box>
              <h4 align={"left"}>Vocabulary Related Objectives</h4>
              <h5>
                Language of Instruction (used by teacher, comprehended by
                students)
              </h5>
              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="languageOfInstruction"
              >
                <EmptyFallback
                  value={R.pathOr(
                    "",
                    [
                      "learningObjectiveForm",
                      "vocabularyObjective",
                      "languageOfInstruction",
                    ],
                    lessonPlanData
                  )}
                  fallback="No response added"
                >
                  {R.pathOr(
                    "",
                    [
                      "learningObjectiveForm",
                      "vocabularyObjective",
                      "languageOfInstruction",
                    ],
                    lessonPlanData
                  )}
                </EmptyFallback>
              </Box>
              <h5>Student Vocabulary (internalised and use in context)</h5>
              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="studentVocabulary"
              >
                <EmptyFallback
                  value={R.pathOr(
                    "",
                    [
                      "learningObjectiveForm",
                      "vocabularyObjective",
                      "studentVocabulary",
                    ],
                    lessonPlanData
                  )}
                  fallback="No response added"
                >
                  {R.pathOr(
                    "",
                    [
                      "learningObjectiveForm",
                      "vocabularyObjective",
                      "studentVocabulary",
                    ],
                    lessonPlanData
                  )}
                </EmptyFallback>
              </Box>
              <br />
              <br />
              <h3 className={classes.heading} align={"left"}>
                LEVEL/S OF LEARNING
              </h3>
              <Box>
                <h4 align={"left"}>All learners will be able to:</h4>
                <EmptyFallback
                  value={R.pathOr(
                    "",
                    ["levelOfLeaningForm", "levelOfLearningInput1"],
                    lessonPlanData
                  )}
                  fallback="No response added"
                >
                  <Typography style={{ whiteSpace: "pre-line" }}>
                    {R.pathOr(
                      "",
                      ["levelOfLeaningForm", "levelOfLearningInput1"],
                      lessonPlanData
                    )}
                  </Typography>
                </EmptyFallback>
                <h4 align={"left"}>Most learners should be able to:</h4>
                <EmptyFallback
                  value={R.pathOr(
                    "",
                    ["levelOfLeaningForm", "levelOfLearningInput2"],
                    lessonPlanData
                  )}
                  fallback="No response added"
                >
                  <Typography style={{ whiteSpace: "pre-line" }}>
                    {R.pathOr(
                      "",
                      ["levelOfLeaningForm", "levelOfLearningInput2"],
                      lessonPlanData
                    )}
                  </Typography>
                </EmptyFallback>
                <h4 align={"left"}>Some learners may be able to:</h4>
                <EmptyFallback
                  value={R.pathOr(
                    "",
                    ["levelOfLeaningForm", "levelOfLearningInput3"],
                    lessonPlanData
                  )}
                  fallback="No response added"
                >
                  <Typography style={{ whiteSpace: "pre-line" }}>
                    {R.pathOr(
                      "",
                      ["levelOfLeaningForm", "levelOfLearningInput3"],
                      lessonPlanData
                    )}
                  </Typography>
                </EmptyFallback>
              </Box>
              <h3 className={classes.heading} align={"left"}>
                Introducing New Knowledge (How do you present the material for
                maximum retention?)
              </h3>
              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="introNewKnowledgeInput"
              >
                <EmptyFallback
                  value={R.pathOr("", ["introNewKnowledgeInput"], lessonPlanData)}
                  fallback="No response added"
                >
                  {R.pathOr("", ["introNewKnowledgeInput"], lessonPlanData)}
                </EmptyFallback>
              </Box>
              <h3 className={classes.heading} align={"left"}>
                Developing/Facilitating New Knowledge (How do you help them make
                connections?)
              </h3>

              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="developNewKnowledgeInput"
              >
                <EmptyFallback
                  value={R.pathOr(
                    "",
                    ["developNewKnowledgeInput"],
                    lessonPlanData
                  )}
                  fallback="No response added"
                >
                  {R.pathOr("", ["developNewKnowledgeInput"], lessonPlanData)}
                </EmptyFallback>
              </Box>

              <h3 className={classes.heading} align={"left"}>
                Student Practice/Feedback/Formative Assessment
              </h3>

              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="studentPracticeAssesmentInput"
              >
                <EmptyFallback
                  value={R.pathOr(
                    "",
                    ["studentPracticeAssesmentInput"],
                    lessonPlanData
                  )}
                  fallback="No response added"
                >
                  {R.pathOr(
                    "",
                    ["studentPracticeAssesmentInput"],
                    lessonPlanData
                  )}
                </EmptyFallback>
              </Box>

              <h3 className={classes.heading} align={"left"}>
                Closure (Retention of New Learning)
              </h3>
              <Box
                style={{ whiteSpace: "pre-line" }}
                fullWidth
                id="closureInput"
              >
                <EmptyFallback
                  value={R.pathOr("", ["closureInput"], lessonPlanData)}
                  fallback="No response added"
                >
                  {R.pathOr("", ["closureInput"], lessonPlanData)}
                </EmptyFallback>
              </Box>
              <h3 className={classes.heading} align={"left"}>
                CONSENTS
              </h3>
              <ConsentForm
                page={"lessonPlan"}
                userRoles={userRoles}
                consentData={R.propOr([], "consents", lessonPlanData)}
                isReadOnly={true}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default LessonPlanReadView;
